import React, { useMemo, useState } from 'react';
import cx from 'classnames';

import { constants, getTextDialFilterData, locale } from '@trqk/utils';

import { mapChartTracksToGenreBuckets } from '../../helpers';
import { BroadcastVsDigitalFilter, Dropdown } from '../../..';
import {
    ChartInfoTooltip,
    EarningsPerMusicUserGenreV2,
    SplitsPerChartTrack,
    ChartOrEmptyMessage,
} from '../..';

interface StatementEarningsProps {
    /**
     * A unique ID for this data set
     */
    id: string;
    /**
     * Height of the container element
     */
    containerHeight: number;
    /**
     * PRO dashboard data
     */
    dashboard: ProIngestionDashboard;
    /**
     * Whether to filter and slice the chart data
     */
    truncateChartData: boolean;
}

const {
    broadcastVsDigitalOptions,
    broadcastOption,
    broadcastFilterPredicate,
    digitalFilterPredicate,
} = constants;
const trackEarnings = 'trackEarnings' as StatementEarningsChartType;
const ownershipShares = 'ownershipShares' as StatementEarningsChartType;
const dropdownOptions = [
    {
        label: locale.enUS.STATEMENTS.SECTIONS.EARNINGS_AND_RANGES
            .TRACK_EARNINGS,
        value: trackEarnings,
    },
    {
        label: locale.enUS.STATEMENTS.SECTIONS.EARNINGS_AND_RANGES
            .OWNERSHIP_SHARES,
        value: ownershipShares,
    },
];
const defaultChartIndex = 0;
const filterKeys: BroadcastOrDigital[] = ['broadcast', 'digital'];
const maxSize = 10;
const minValue = 0.01;
const chartHeightOffset = 130;

/**
 * Displays one of two charts for a given PRODashboard, based on the state of a dropdown.
 * - Track Earnings: stacked bar chart showing earnings per track, with each bar split by
 * music_user_genre_name
 * - Ownership Shares: stacked bar chart showing earnings per track, with each bar split by
 * ownership share
 * @param props {StatementEarningsProps}
 */
export const StatementEarnings = ({
    containerHeight,
    dashboard,
    id,
    truncateChartData,
}: StatementEarningsProps): JSX.Element => {
    const [chartType, setChartType] = useState<StatementEarningsChartType>(
        dropdownOptions[defaultChartIndex].value,
    );
    const chartTracks = dashboard?.chart_tracks ?? null;

    const chartTrackPerGenreBucket = useMemo(
        () => mapChartTracksToGenreBuckets(chartTracks),
        [chartTracks],
    );

    const ownershipSharesData = useMemo(
        () =>
            getTextDialFilterData<ChartTrackPerGenreBucket>({
                data: chartTrackPerGenreBucket,
                filterConfigs: {
                    broadcast: {
                        filterPredicate: broadcastFilterPredicate,
                        maxSize,
                        sort: {
                            key: 'splits_total_earnings',
                            order: 'desc',
                        },
                        smallValueFilter: {
                            key: 'splits_total_earnings',
                            min: minValue,
                        },
                    },
                    digital: {
                        filterPredicate: digitalFilterPredicate,
                        maxSize,
                        sort: {
                            key: 'splits_total_earnings',
                            order: 'desc',
                        },
                        smallValueFilter: {
                            key: 'splits_total_earnings',
                            min: minValue,
                        },
                    },
                },
                defaultFilterOrder: ['broadcast', 'digital'],
                truncateChartData,
            }),
        [chartTrackPerGenreBucket],
    );

    const defaultFilterOption =
        broadcastVsDigitalOptions.get(
            (ownershipSharesData?.defaultFilter ??
                'broadcast') as BroadcastOrDigital,
        ) ?? broadcastOption;

    const [filterOption, setFilterOption] =
        useState<DropdownOption<BroadcastOrDigital>>(defaultFilterOption);

    const chartHeight = containerHeight - chartHeightOffset;

    return (
        <div style={{ height: containerHeight }}>
            <div className="pt-4 pl-4 mb-6">
                <div className="flex justify-end mr-4">
                    <BroadcastVsDigitalFilter
                        filterOption={filterOption}
                        setFilterOption={setFilterOption}
                    />
                </div>
                <div className="text-sm text-tertiary-400 mb-1">
                    {locale.enUS.SELECT_CHART}
                </div>
                <div className="flex justify-between items-end">
                    <Dropdown
                        defaultIndex={defaultChartIndex}
                        size="medium"
                        options={dropdownOptions}
                        handleChange={(
                            value: StatementEarningsChartType,
                        ): void => {
                            setChartType(value);
                        }}
                        width={200}
                    />
                    <div className="mr-4">
                        <ChartInfoTooltip
                            maxSize={maxSize}
                            minValue={minValue}
                        />
                    </div>
                </div>
            </div>
            {/* Track Earnings */}
            <div
                className={cx('mr-2', {
                    hidden: chartType !== trackEarnings,
                })}
            >
                {filterKeys.map(
                    (filterKey: BroadcastOrDigital): JSX.Element => (
                        <div
                            key={filterKey}
                            className={cx({
                                hidden: filterOption.value !== filterKey,
                            })}
                        >
                            <EarningsPerMusicUserGenreV2
                                chartDivId={`${id}.statement-earnings-per-genre.${filterKey}`}
                                dashboard={dashboard}
                                height={chartHeight}
                                genre={filterKey}
                                maxSize={maxSize}
                                minValue={minValue}
                            />
                        </div>
                    ),
                )}
            </div>
            {/* Ownership Shares */}
            <div
                className={cx('mr-2', {
                    hidden: chartType !== ownershipShares,
                })}
            >
                {filterKeys.map(
                    (filterKey: BroadcastOrDigital): JSX.Element => (
                        <div
                            key={filterKey}
                            className={cx({
                                hidden: filterOption.value !== filterKey,
                            })}
                        >
                            <ChartOrEmptyMessage
                                data={
                                    ownershipSharesData.filteredData[filterKey]
                                        .data
                                }
                                height={chartHeight}
                            >
                                <SplitsPerChartTrack
                                    chartDivId={`${id}.statement-splits-per-genre.${filterKey}`}
                                    height={chartHeight}
                                    chartTrackPerGenreBucket={
                                        ownershipSharesData.filteredData[
                                            filterKey
                                        ].data
                                    }
                                />
                            </ChartOrEmptyMessage>
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};
